import {
    Chip,
    TextField
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from "react";
import { connect } from "react-redux";
import { getAreaByTalukaPostBodyUpdated } from "../../Slice/area.slice";
import { getDistrictByZone } from "../../Slice/district.slice";
import { getRegionNew } from "../../Slice/region.slice";
import { getStateByRegion } from "../../Slice/state.slice";
import { getTalukaByDistrict } from "../../Slice/taluka.slice";
import { getZoneByState } from "../../Slice/zone.slice";
import AutocompleteCompo from "../../components/Comman/AutoCompleteCompoForScheme";
import MultiSelectPopover from "../../components/Comman/PopoverCommon";
import { blueFadeColor, lightWhiteFadeColor, redColor, white } from "../../config/ColorObj";
import { serverMsg } from "../../config/messageconstant";
import { showLoador, showNotification } from "../Landing/Landing.slice";
import "./Scheme.css";

/* ------------------ STYLED INPUT (UNCHANGED) ------------------ */
const CustomTextField = styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        height: 42,
        borderRadius: "10px",
        transition: "border-color 0.2s ease",
        "& fieldset": { borderColor: theme.palette.grey[300] },
        "&:hover fieldset": { borderColor: blueFadeColor },
        "&.Mui-focused fieldset": {
            borderColor: blueFadeColor,
            borderWidth: 1.5,
        },
    },
    "& .MuiOutlinedInput-input": {
        padding: "8px 10px",
        fontSize: "14px",
    },
    "& .MuiOutlinedInput-root.Mui-error fieldset": {
        borderColor: redColor,
    },
}));

/* ------------------ COMPONENT ------------------ */
const SchemeScope = forwardRef((props, ref) => {
    const {
        regionList,
        stateList,
        zoneList,
        districtList,
        talukaList,
        areaList,
        showLoador,
        showNotification,
        getRegionNew,
        getStateByRegion,
        getZoneByState,
        getDistrictByZone,
        getTalukaByDistrict,
        getAreaByTalukaPostBodyUpdated,
    } = props;

    const [scope, setScope] = useState(0);
    const scopeOptions = [
        { label: "Area Wise", value: 0 },
        { label: "Pan India", value: 1 },
    ];

    const [selection, setSelection] = useState({
        region: null,
        state: null,
        zone: [],
        district: [],
        taluka: [],
        area: [],
    });

    const [regionInput, setRegionInput] = useState("");
    const [stateInput, setStateInput] = useState("");
    const [errors, setErrors] = useState({
        region: "",
        state: "",
        zone: "",
        district: "",
        taluka: "",
        area: "",
    });
    const [shake, setShake] = useState({
        region: false,
        state: false,
        zone: false,
        district: false,
        taluka: false,
        area: false,
    });

    /* ----------- POPOVER STATE (NO DUPLICATE DATA) ----------- */
    const [popoverAnchor, setPopoverAnchor] = useState(null);
    const [popoverType, setPopoverType] = useState(null);

    const openPopover = Boolean(popoverAnchor);

    const handleOpenPopover = (event, type) => {
        setPopoverAnchor(event.currentTarget);
        setPopoverType(type);
    };

    const handleClosePopover = () => {
        setPopoverAnchor(null);
        setPopoverType(null);
    };

    /* ------------------ SAFE OPTIONS ------------------ */
    const regionOptions = useMemo(
        () => (Array.isArray(regionList?.region) ? regionList.region : []),
        [regionList?.region]
    );

    const stateOptions = useMemo(
        () =>
            Array.isArray(stateList?.stateByRegion) ? stateList.stateByRegion : [],
        [stateList?.stateByRegion]
    );

    const zoneOptions = useMemo(
        () => (Array.isArray(zoneList?.zoneByState) ? zoneList.zoneByState : []),
        [zoneList?.zoneByState]
    );

    const districtOptions = useMemo(
        () =>
            Array.isArray(districtList?.districtByZone)
                ? districtList.districtByZone
                : [],
        [districtList?.districtByZone]
    );

    const talukaOptions = useMemo(
        () =>
            Array.isArray(talukaList?.talukaByDistrict)
                ? talukaList.talukaByDistrict
                : [],
        [talukaList?.talukaByDistrict]
    );

    const areaOptions = useMemo(
        () => (Array.isArray(areaList?.areaByTaluka) ? areaList.areaByTaluka : []),
        [areaList?.areaByTaluka]
    );

    /* ------------------ INITIAL LOAD ------------------ */
    useEffect(() => {
        showLoador({ loador: true });
        getRegionNew().finally(() => showLoador({ loador: false }));
    }, []);

    /* ------------------ API WRAPPER ------------------ */
    const callApi = async (apiFn, payload) => {
        showLoador({ loador: true });
        const res = await apiFn(payload);
        showLoador({ loador: false });

        if (!res?.success) {
            showNotification({ msg: serverMsg, severity: "error" });
        }
    };

    /* ------------------ SINGLE SELECT ------------------ */
    const handleSingleSelect = useCallback(async (type, value, reason) => {

        setSelection((prev) => ({
            ...prev,
            [type]: value,
            ...(type === "region" && {
                state: null,
                zone: [],
                district: [],
                taluka: [],
                area: [],
            }),
            ...(type === "state" && {
                zone: [],
                district: [],
                taluka: [],
                area: [],
            }),
        }));

        if (reason === "clear") {
            setErrors((prev) => ({
                ...prev,
                [type]: "",
            }));

            setShake((prev) => ({
                ...prev,
                [type]: false,
            }));
        }

        if (!value) return;

        if (type === "region") {
            await callApi(getStateByRegion, { regionIds: value.id });
        }

        if (type === "state") {
            await callApi(getZoneByState, { stateIds: value.id });
        }
    }, []);

    /* ------------------ MULTI SELECT ------------------ */
    const handleMultiSelect = useCallback(
        async (type, values) => {

            setSelection((prev) => ({
                ...prev,
                [type]: values,
                ...(type === "zone" && {
                    district: [],
                    taluka: [],
                    area: [],
                }),
                ...(type === "district" && {
                    taluka: [],
                    area: [],
                }),
                ...(type === "taluka" && {
                    area: [],
                }),
            }));

            // stop API calls when cleared
            if (!values || values.length === 0) {
                if (popoverType === type) {
                    handleClosePopover();
                }
                return;
            }
            if (type === "zone") {
                const zoneIds = values.map((z) => z.id);
                await callApi(getDistrictByZone, { zoneIds });
            }

            if (type === "district") {
                const districtIds = values.map((z) => z.id);
                await callApi(getTalukaByDistrict, { districtIds });
            }

            if (type === "taluka") {
                await callApi(getAreaByTalukaPostBodyUpdated, {
                    talukaIds: {
                        talukaIds: values.filter((z) => z?.id).map((z) => z.id),
                    },
                });
            }

            // auto-close popover if empty
            if (values.length === 0 && popoverType === type) {
                handleClosePopover();
            }
        },
        [popoverType]
    );

    /* ------------------ COMMON AUTOCOMPLETE ------------------ */
    const commonAutoProps = {
        size: "small",
        getOptionLabel: (o) => o?.name || "",
        isOptionEqualToValue: (o, v) => o?.id === v?.id,

        sx: {
            "& .MuiInputBase-input": {
                fontSize: "14px",
                fontFamily: "Poppins, Helvetica, Arial, sans-serif",
            },
        },

        componentsProps: {
            paper: {
                sx: {
                    fontSize: "14px",
                    fontFamily: "Poppins, Helvetica, Arial, sans-serif",
                },
            },
            popper: {
                sx: {
                    "& .MuiAutocomplete-option": {
                        fontSize: "14px",
                        fontFamily: "Poppins, Helvetica, Arial, sans-serif",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        margin: "2px 6px",

                        "&.Mui-selected": {
                            backgroundColor: "#4861df !important",
                            color: white,
                        },
                        "&.Mui-selected.Mui-focused": {
                            backgroundColor: "#4861df !important",
                        },
                        "&:hover": {
                            backgroundColor: lightWhiteFadeColor,
                        },
                    },
                },
            },
        },
    };

    const multiProps = {
        multiple: true,
        disableCloseOnSelect: true,
    };

    /* ------------------ EXPANDABLE TAGS ------------------ */
    const renderExpandableTags = (type) => (value, getTagProps) => {
        const visible = value.slice(0, 2);
        const hiddenCount = value.length - visible.length;

        return (
            <>
                {visible.map((option, index) => (
                    <Chip
                        key={option.id}
                        label={option.name}
                        size="small"
                        {...getTagProps({ index })}
                        sx={{
                            height: 22,
                            fontSize: 12,
                            backgroundColor: lightWhiteFadeColor,
                            mr: 0.5,
                        }}
                    />
                ))}

                {hiddenCount > 0 && (
                    <Chip
                        label={`+${hiddenCount}`}
                        size="small"
                        onClick={(e) => handleOpenPopover(e, type)}
                        sx={{
                            height: 22,
                            fontSize: 12,
                            cursor: "pointer",
                            backgroundColor: white,
                            color: blueFadeColor,
                            border: "1px dashed #4861df",
                        }}
                    />
                )}
            </>
        );
    };
    const isEmptyStringOrNull = (value) =>
        value === null || value === undefined || value === "";

    const isEmptyArrayOrString = (value) =>
        (Array.isArray(value) && value.length === 0) || value === "";

    const validate = () => {
        let valid = true;
        const newErrors = {};
        const newShake = {};

        // Validate only when scope === 0
        if (scope !== 0) {
            return { isValid: true, data: selection };
        }

        const rules = {
            region: isEmptyStringOrNull(selection.region),
            state: isEmptyStringOrNull(selection.state),
            zone: isEmptyArrayOrString(selection.zone),
            district: isEmptyArrayOrString(selection.district),
            taluka: isEmptyArrayOrString(selection.taluka),
            area: isEmptyArrayOrString(selection.area),
        };

        Object.keys(rules).forEach((key) => {
            if (rules[key]) {
                valid = false;

                newErrors[key] =
                    `${key.charAt(0).toUpperCase() + key.slice(1)} is required`;

                newShake[key] = true;

                setTimeout(() => {
                    setShake((prev) => ({ ...prev, [key]: false }));
                }, 400);
            }
        });

        setErrors((prev) => ({ ...prev, ...newErrors }));
        setShake((prev) => ({ ...prev, ...newShake }));

        return {
            isValid: valid,
            data: selection,
        };
    };

    useImperativeHandle(ref, () => ({
        validate,
        getData,
    }));

    const getData = () => ({
        scopeType: scope,
        region: selection.region,
        state: selection.state,
        zone: selection.zone,
        district: selection.district,
        taluka: selection.taluka,
        area: selection.area,
    });

    const changePillGroupValue = (value) => {
        setScope(value);
        if (value === 1) {
            // Pan India
            setSelection({
                region: null,
                state: null,
                zone: [],
                district: [],
                taluka: [],
                area: [],
            });

            setErrors({
                region: "",
                state: "",
                zone: "",
                district: "",
                taluka: "",
                area: "",
            });

            setShake({
                region: false,
                state: false,
                zone: false,
                district: false,
                taluka: false,
                area: false,
            });

            setRegionInput("");
            setStateInput("");
        }
    };

    const popoverList = popoverType ? selection[popoverType] : [];

    /* ------------------ JSX ------------------ */
    return (
        <div className="card-scheme">
            <div className="card-title-scheme">📍 Offer Scope</div>

            <label>Offer Applicable For</label>
            <div className="pill-scheme-group">
                {scopeOptions.map((item) => (
                    <div
                        key={item.value}
                        className={`pill-scheme  ${scope === item.value ? "active" : ""}`}
                        onClick={() => changePillGroupValue(item.value)}
                    >
                        {item.label}
                    </div>
                ))}
            </div>

            {scope === 0 && (
                <div className="grid-scheme mt-16">
                    <div className={shake.region ? "shake-error" : ""}>
                        <label className="required-label">Region</label>
                        <AutocompleteCompo
                            {...commonAutoProps}
                            options={regionOptions}
                            value={selection.region}
                            inputValue={regionInput}
                            onInputChange={(e, v) => setRegionInput(v)}
                            onChange={(e, v, reason) => {
                                handleSingleSelect("region", v, reason);
                                setErrors((prev) => ({
                                    ...prev,
                                    region: v ? "" : "Region is required",
                                }));
                            }}
                            renderInput={(p) => (
                                <CustomTextField
                                    {...p}
                                    placeholder="Select Region"
                                    error={Boolean(errors.region)} //red border
                                    helperText={errors.region}
                                />
                            )}
                        />
                    </div>
                    <div className={shake.state ? "shake-error" : ""}>
                        <label className="required-label">State</label>
                        <AutocompleteCompo
                            {...commonAutoProps}
                            options={stateOptions}
                            value={selection.state}
                            inputValue={stateInput}
                            onInputChange={(e, v) => setStateInput(v)}
                            onChange={(e, v, reason) => {
                                handleSingleSelect("state", v, reason);
                                setErrors((prev) => ({
                                    ...prev,
                                    state: v ? "" : "State is required",
                                }));
                            }}
                            renderInput={(p) => (
                                <CustomTextField
                                    {...p}
                                    placeholder="Select State"
                                    error={Boolean(errors.state)} // red border
                                    helperText={errors.state}
                                />
                            )}
                        />
                    </div>
                    <div className={shake.zone ? "shake-error" : ""}>
                        <label className="required-label">Zone</label>
                        <AutocompleteCompo
                            {...commonAutoProps}
                            {...multiProps}
                            options={zoneOptions}
                            value={selection.zone}
                            renderTags={renderExpandableTags("zone")}
                            onChange={(e, v) => {
                                handleMultiSelect("zone", v);
                                setErrors((prev) => ({
                                    ...prev,
                                    zone: v ? "" : "Zone is required",
                                }));
                            }}

                            enableSelectActions
                            onSelectAll={() => {
                                handleMultiSelect("zone", zoneOptions);
                                setErrors((prev) => ({
                                    ...prev,
                                    zone: "",
                                }));
                            }}
                            onRemoveAll={() => {
                                handleMultiSelect("zone", []);
                                setErrors((prev) => ({
                                    ...prev,
                                    zone: "Zone is required",
                                }));
                            }}

                            placeholder="Select Zone"
                            error={errors.zone}
                            helperText={errors.zone}
                        />

                    </div>
                    <div className={shake.district ? "shake-error" : ""}>
                        <label className="required-label">District</label>
                        <AutocompleteCompo
                            {...commonAutoProps}
                            {...multiProps}
                            options={districtOptions}
                            value={selection.district}
                            renderTags={renderExpandableTags("district")}
                            onChange={(e, v) => {
                                handleMultiSelect("district", v);
                                setErrors((prev) => ({
                                    ...prev,
                                    district: v ? "" : "District is required",
                                }));
                            }}

                            enableSelectActions
                            onSelectAll={() => {
                                handleMultiSelect("district", districtOptions);
                                setErrors((prev) => ({
                                    ...prev,
                                    district: "",
                                }));
                            }}
                            onRemoveAll={() => {
                                handleMultiSelect("district", []);
                                setErrors((prev) => ({
                                    ...prev,
                                    district: "District is required",
                                }));
                            }}

                            placeholder="Select District"
                            error={errors.district}
                            helperText={errors.district}
                        />
                    </div>

                    <div className={shake.taluka ? "shake-error" : ""}>
                        <label className="required-label">Taluka</label>

                        <AutocompleteCompo
                            {...commonAutoProps}
                            {...multiProps}
                            options={talukaOptions}
                            value={selection.taluka}
                            renderTags={renderExpandableTags("taluka")}
                            onChange={(e, v) => {
                                handleMultiSelect("taluka", v);
                                setErrors((prev) => ({
                                    ...prev,
                                    taluka: v ? "" : "Taluka is required",
                                }));
                            }}

                            enableSelectActions
                            onSelectAll={() => {
                                handleMultiSelect("taluka", talukaOptions);
                                setErrors((prev) => ({
                                    ...prev,
                                    taluka: "",
                                }));
                            }}
                            onRemoveAll={() => {
                                handleMultiSelect("taluka", []);
                                setErrors((prev) => ({
                                    ...prev,
                                    taluka: "Taluka is required",
                                }));
                            }}

                            placeholder="Select Taluka"
                            error={errors.taluka}
                            helperText={errors.taluka}
                        />
                    </div>

                    <div className={shake.area ? "shake-error" : ""}>
                        <label className="required-label">Area</label>

                        <AutocompleteCompo
                            {...commonAutoProps}
                            {...multiProps}
                            options={areaOptions}
                            value={selection.area}
                            renderTags={renderExpandableTags("area")}
                            onChange={(e, v) => {
                                handleMultiSelect("area", v);
                                setErrors((prev) => ({
                                    ...prev,
                                    area: v ? "" : "Area is required",
                                }));
                            }}

                            enableSelectActions
                            onSelectAll={() => {
                                handleMultiSelect("area", areaOptions);
                                setErrors((prev) => ({
                                    ...prev,
                                    area: "",
                                }));
                            }}
                            onRemoveAll={() => {
                                handleMultiSelect("area", []);
                                setErrors((prev) => ({
                                    ...prev,
                                    area: "Area is required",
                                }));
                            }}

                            placeholder="Select Area"
                            error={errors.area}
                            helperText={errors.area}
                        />
                    </div>
                </div>
            )}

            {/* ---------- POPOVER ---------- */}
            <MultiSelectPopover
                open={openPopover}
                anchorEl={popoverAnchor}
                items={popoverList}
                emptyText="No items selected"
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                onClose={handleClosePopover}
                onRemove={(item) => {
                    handleMultiSelect(
                        popoverType,
                        popoverList.filter((x) => x.id !== item.id)
                    );
                }}
            />

        </div>
    );
});

const mapStateToProps = (state) => ({
    regionList: state.region,
    stateList: state.states,
    zoneList: state.zone,
    districtList: state.district,
    talukaList: state.taluka,
    areaList: state.area,
});

const mapDispatchToProps = {
    getRegionNew,
    getStateByRegion,
    getZoneByState,
    getDistrictByZone,
    getTalukaByDistrict,
    getAreaByTalukaPostBodyUpdated,
    showLoador,
    showNotification,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {
    forwardRef: true,
})(SchemeScope);